import React, { useRef } from 'react';
import { Card, Form, Input, Button, message } from 'antd';
import type ReCAPTCHA from 'react-google-recaptcha';
import { MailOutlined, EnvironmentOutlined, PhoneOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { RecaptchaField } from '../../components/common/RecaptchaField';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import apiClient from '../../api/axios';
import { PageHero } from '../../components/common/PageHero';

const Contact: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    // Fetch page metadata and update title
    const { data: pageMeta } = usePageMetadata('kontakt');

    const { data: contactInfo, isLoading } = useQuery({
        queryKey: ['publicContactInfo', i18n.language],
        queryFn: async () => {
            const response = await apiClient.get(`/public/contact/info?lang=${i18n.language}`);
            return response.data.data;
        }
    });

    const onFinish = async (values: any) => {
        try {
            // Manual reCAPTCHA execution for invisible mode
            const token = await recaptchaRef.current?.executeAsync();
            if (!token) {
                messageApi.error(t('verify_recaptcha', 'Bitte bestätigen Sie, dass Sie kein Roboter sind'));
                return;
            }
            
            await apiClient.post('/public/contact/submit', { ...values, recaptchaToken: token });
            messageApi.success(t('contact_success', 'Ihre Nachricht wurde erfolgreich gesendet! Wir werden uns bald bei Ihnen melden.'));
            form.resetFields();
            recaptchaRef.current?.reset();
        } catch (error: any) {
            console.error('Error submitting contact form:', error);
            if (error.response?.data?.detail?.includes('reCAPTCHA') || error.response?.data?.errors?.RecaptchaToken?.length > 0) {
                messageApi.error(t('recaptcha_error', 'reCAPTCHA Validierung fehlgeschlagen. Bitte versuchen Sie es erneut.'));
            } else {
                messageApi.error(t('contact_error', 'Fehler beim Senden der Nachricht.'));
            }
        }
    };

    const inputStyle = { 
        backgroundColor: '#f5f5f5', 
        padding: '16px 20px', 
        fontSize: '16px',
        borderRadius: '0px' 
    };

    return (
        <div className="flex flex-col bg-background min-h-screen">
            <PageHero
                title={pageMeta?.title || t('contact_title', 'Kontakt')}
                subtitle={pageMeta?.metaDescription || t('contact_subtitle', 'Wir freuen uns auf Ihre Nachricht und stehen Ihnen für Fragen gerne zur Verfügung.')}
                breadcrumb={[
                    { label: t('common.home'), to: '/' },
                    { label: pageMeta?.title || t('contact_breadcrumb', 'Kontakt') }
                ]}
            />

            <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8 w-full">
                {contextHolder}
            {/* Contact Information Section - Moved to Top */}
            {!isLoading && contactInfo && (
                <Card className="shadow-none border-none bg-white p-0 sm:p-2 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h2 className="text-2xl font-bold mb-6 border-b-2 border-primary w-fit pb-1">{t('contact_info_title', 'Kontaktinformationen')}</h2>
                            <div className="space-y-4">
                                <p className="flex items-start gap-3">
                                    <EnvironmentOutlined className="text-primary mt-1 text-lg" />
                                    <span><strong>{t('address', 'Adresse')}:</strong> <br/> {contactInfo.address}</span>
                                </p>
                                <p className="flex items-center gap-3">
                                    <MailOutlined className="text-primary text-lg" />
                                    <span><strong>{t('email', 'E‑Mail')}:</strong> <br/> <a href={`mailto:${contactInfo.email}`} className="text-primary hover:underline">{contactInfo.email}</a></span>
                                </p>
                                <p className="flex items-center gap-3">
                                    <PhoneOutlined className="text-primary text-lg" />
                                    <span><strong>{t('phone', 'Telefon')}:</strong> <br/> <a href={`tel:${contactInfo.phone?.replace(/\s/g, '')}`} className="text-primary hover:underline">{contactInfo.phone}</a></span>
                                </p>
                                <p className="flex items-start gap-3">
                                    <ClockCircleOutlined className="text-primary mt-1 text-lg" />
                                    <span><strong>{t('opening_hours', 'Öffnungszeiten')}:</strong> <br/> {contactInfo.openingHours}</span>
                                </p>
                            </div>
                        </div>
                        {contactInfo.mapUrl && (
                            <div className="w-full h-80 rounded-lg overflow-hidden shadow-md">
                                <iframe
                                    src={contactInfo.mapUrl}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen={true}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                ></iframe>
                            </div>
                        )}
                    </div>
                </Card>
            )}

            <Card className="shadow-none border-none bg-white p-0 sm:p-2">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    requiredMark={false}
                    className="w-full"
                >
                    <div className="grid grid-cols-1 gap-x-2 gap-y-2 sm:grid-cols-2">
                        <Form.Item name="firstName" className="mb-0" rules={[{ required: true, message: t('required', 'Bitte ausfüllen') }]}>
                            <Input variant="borderless" style={inputStyle} placeholder={`${t('firstname_upper', 'VORNAME')} *`} />
                        </Form.Item>
                        <Form.Item name="lastName" className="mb-0">
                            <Input variant="borderless" style={inputStyle} placeholder={t('lastname_upper', 'NACHNAME')} />
                        </Form.Item>

                        <Form.Item name="email" className="mb-0" rules={[{ required: true, type: 'email', message: t('invalid_email', 'Ungültige E-Mail') }]}>
                            <Input variant="borderless" style={inputStyle} placeholder={`${t('email_upper', 'E-MAIL')}*`} />
                        </Form.Item>
                        <Form.Item name="phone" className="mb-0">
                            <Input variant="borderless" style={inputStyle} placeholder={t('phone_upper', 'TELEFONNUMMER')} />
                        </Form.Item>
                    </div>

                    <Form.Item name="subject" className="mt-2 mb-0">
                        <Input variant="borderless" style={inputStyle} placeholder={t('subject_upper', 'BETREFF')} />
                    </Form.Item>

                    <Form.Item name="message" className="mt-2 mb-4" rules={[{ required: true, message: t('required', 'Bitte ausfüllen') }]}>
                        <Input.TextArea variant="borderless" style={{ ...inputStyle, minHeight: '160px' }} placeholder={t('message_upper', 'NACHRICHT')} />
                    </Form.Item>

                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mt-8">
                        <div>
                            <Form.Item name="recaptchaToken" className="mb-0">
                                <RecaptchaField ref={recaptchaRef} />
                            </Form.Item>
                        </div>
                        <div className="w-full">
                            <Form.Item className="mb-0">
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    size="large" 
                                    block 
                                    icon={<MailOutlined />}
                                    className="h-16 text-lg font-bold tracking-wider rounded-none"
                                >
                                    {t('send_upper', 'SENDEN')}
                                </Button>
                            </Form.Item>
                        </div>
                    </div>
                </Form>
            </Card>
        </div>
    </div>
    );
};

export default Contact;
